import { useMemo } from "react";
import { DrawerBody } from "@chakra-ui/react";
import { pick } from "lodash";
import { useMutation } from "@apollo/client";
import { ADMIN_CONTEXT } from "utils/apollo/hooks/admin";
import { useActiveBrandIdContext } from "utils/apollo/hooks/brand";
import { addressFieldsFragment } from "components/Shared/Address";
import DrawerFormFooter from "components/Shared/DrawerForm/DrawerFormFooter";
import {
  addressFields,
  getUpdateOrCreateVariables,
} from "components/Shared/Forms/AddressFieldset";
import {
  phoneNumberFragment,
  phoneNumberFields,
  getUpdateOrCreatePhoneNumberVariables,
} from "components/Shared/Forms/PhoneInput";
import useCreateValidSubmitHandler from "components/Shared/Forms/useCreateValidSubmitHandler";
import { assertPresence } from "utils/assertions";
import { FragmentType, getFragmentData, gql } from "__generated__/gql";
import Form, {
  retailerFields,
  RetailerFormFields,
  processApolloError,
} from "./Form";

const FORM_ID = "update-retailer-form";

const fragment = gql(/* GraphQL */ `
  fragment EditRetailerDetails on Retailer {
    id
    numStores
    name
    uniqueSuffix
    retailerType
    slottingRequired
    pricingStrategy
    categoryReview
    tags
    affiliation
    notes
    forBrandId
    parentId
    parent {
      id
      ...RetailerSelectOption
    }
    regionsList {
      id
      ...RetailerRegionSelectOption
    }
    channelsList {
      id
      ...RetailerChannelSelectOption
    }
    headquartersAddress {
      id
      ...AddressComponentFields
    }
    headquartersPhoneNumber {
      id
      ...PhoneNumberInputFields
    }
    distributionCentersList(orderBy: FULL_DISPLAY_NAME_ASC) {
      id
      ...DistributionCenterSelectOption
    }
  }
`);

const updateMutation = gql(/* GraphQL */ `
  mutation UpdateRetailer(
    $id: UUID!
    $data: RetailerPatch!
    $isAddressUpdatedOrCreated: Boolean = false
    $addressId: UUID
    $addressData: AddressPatch
    $isPhoneNumberUpdatedOrCreated: Boolean = false
    $phoneNumberId: UUID
    $phoneNumberData: PhoneNumberPatch
    $retailerChannelIds: [UUID!]!
    $retailerRegionIds: [UUID!]!
    $distributionCenterIds: [UUID!]!
  ) {
    ...UpdateOrCreateAddress
    ...UpdateOrCreatePhoneNumber
    updateRetailer(input: { id: $id, patch: $data }) {
      retailer {
        id
      }
    }
    ...UpdateRetailerManyToMany
  }
`);

type Props = {
  data: FragmentType<typeof fragment> | null | undefined;
  isAdmin?: boolean;
  isLoading?: boolean;
  onCloseEdit: () => void;
};

export default function EditRetailerDetails({
  data,
  isAdmin,
  isLoading,
  onCloseEdit,
}: Props) {
  const retailer = getFragmentData(fragment, data);

  const defaultValues = useMemo(() => {
    if (!retailer) return;
    return {
      ...pick(retailer, retailerFields),
      headquartersAddress: pick(
        getFragmentData(addressFieldsFragment, retailer.headquartersAddress),
        addressFields,
      ),
      headquartersPhoneNumber: pick(
        getFragmentData(phoneNumberFragment, retailer.headquartersPhoneNumber),
        phoneNumberFields,
      ),
      channelIds: retailer.channelsList?.map((c) => c.id) || [],
      regionIds: retailer.regionsList?.map((c) => c.id) || [],
      distributionCenterIds:
        retailer.distributionCentersList?.map((dc) => dc.id) || [],
    };
  }, [retailer]);

  const brandContext = useActiveBrandIdContext();

  const [performUpdate, { loading: isUpdating }] = useMutation(updateMutation, {
    context: isAdmin ? ADMIN_CONTEXT : brandContext,
  });

  const [handleValidSubmit] = useCreateValidSubmitHandler(
    (formData: RetailerFormFields) => {
      assertPresence(retailer?.id, "Expected ID to be available!");

      const {
        headquartersAddress,
        headquartersPhoneNumber,
        channelIds,
        regionIds,
        distributionCenterIds,
        ...patchData
      } = formData;

      const addressVariables = getUpdateOrCreateVariables(
        retailer.headquartersAddress?.id,
        headquartersAddress,
      );

      const phoneNumberVariables = getUpdateOrCreatePhoneNumberVariables(
        retailer.headquartersPhoneNumber?.id,
        headquartersPhoneNumber,
      );

      return performUpdate({
        variables: {
          id: retailer.id,
          data: {
            ...patchData,
            headquartersAddressId: addressVariables.addressId,
            headquartersPhoneNumberId: phoneNumberVariables.phoneNumberId,
          },
          ...addressVariables,
          ...phoneNumberVariables,
          retailerChannelIds: channelIds,
          retailerRegionIds: regionIds,
          distributionCenterIds,
        },
      });
    },
    {
      processApolloError,
      successMessage: "Retailer updated successfully.",
    },
  );

  return (
    <>
      <DrawerBody>
        <Form
          formId={FORM_ID}
          defaultValues={defaultValues}
          isAdmin={isAdmin}
          onValidSubmit={(formData, context) =>
            handleValidSubmit(formData, context).then(() => onCloseEdit())
          }
          isLoaded={!isLoading}
          parentSelectFilter={
            retailer
              ? {
                  id: { notEqualTo: retailer.id },
                  forBrandId: { equalTo: retailer.forBrandId },
                }
              : undefined
          }
        />
      </DrawerBody>
      <DrawerFormFooter
        onClose={onCloseEdit}
        formId={FORM_ID}
        submitButtonProps={{
          isLoading: isUpdating,
        }}
      />
    </>
  );
}
